import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';

const principles = [
  'Clarity before activity', 'Evidence before assumption', 'Strategy before production',
  'Human judgement strengthened by technology', 'Creativity connected to measurable purpose',
  'Responsible use of artificial intelligence', 'Long-term client value over short-term noise'
];

export default function About() {
  return (
    <div className="bg-black pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-4xl mb-32">
          <h1 className="text-6xl md:text-8xl font-serif font-bold mb-8 leading-[1.1]">Creativity Is Stronger When It Begins With Clarity.</h1>
          <p className="text-2xl text-zinc-400 font-light leading-relaxed">
            Maxmark is an independent creative and digital agency based in Lagos, Nigeria. We combine strategic thinking, competitor intelligence, design, technology, production and responsible AI to help organisations build stronger brands and make better marketing decisions.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-24 mb-40">
          <section>
            <h2 className="text-xs font-bold tracking-[0.4em] text-blue-500 uppercase mb-10">Our Perspective</h2>
            <div className="space-y-8 text-xl text-zinc-300 leading-relaxed font-light">
              <p>Many organisations are busy producing content, running campaigns and adopting new technology, yet remain uncertain about what the market is doing and which action deserves priority. We believe activity should follow strategy—and strategy should be informed by evidence.</p>
              <p>That is why Maxmark brings intelligence and execution together. We help clients see their competitive environment, interpret what matters, choose a coherent direction and produce the work required to move forward.</p>
            </div>
          </section>
          
          <div className="space-y-20">
            <section>
              <h2 className="text-xs font-bold tracking-[0.4em] text-zinc-500 uppercase mb-8">Mission</h2>
              <p className="text-3xl font-serif font-bold">To give ambitious brands the intelligence, strategy and creative systems they need to compete confidently and grow responsibly.</p>
            </section>
            <section>
              <h2 className="text-xs font-bold tracking-[0.4em] text-zinc-500 uppercase mb-8">Vision</h2>
              <p className="text-3xl font-serif font-bold">To become Africa’s most trusted intelligence-led creative partner for brands shaping the future.</p>
            </section>
          </div>
        </div>

        <section className="py-24 bg-zinc-950 rounded-[3rem] p-12 md:p-24 border border-zinc-900 mb-40">
          <h2 className="text-xs font-bold tracking-[0.4em] text-zinc-500 uppercase mb-16 text-center">Operating Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((p, i) => (
              <div key={i} className="flex items-center gap-6 p-6 border border-zinc-900 rounded-2xl hover:bg-zinc-900 transition-colors">
                <span className="text-blue-500 text-lg font-serif">0{i+1}</span>
                <span className="text-lg font-medium text-white">{p}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-40">
          <div>
            <h2 className="text-4xl font-serif font-bold mb-8">Built in Lagos. Designed to Travel.</h2>
            <p className="text-xl text-zinc-400 leading-relaxed">
              Our location gives us proximity to one of the world’s most energetic cultural and commercial markets. Our systems, collaboration and production standards allow us to serve organisations across Africa and beyond.
            </p>
          </div>
          <div className="aspect-video rounded-3xl overflow-hidden grayscale">
            <img src="https://images.unsplash.com/photo-1596434449272-972740926830?auto=format&fit=crop&w=1000&q=80" alt="Lagos" className="w-full h-full object-cover" />
          </div>
        </section>

        <section className="text-center">
          <Link to="/contact" className="bg-blue-600 text-white px-12 py-5 rounded font-bold uppercase tracking-widest text-xs inline-block">
            Build With Maxmark
          </Link>
        </section>
      </div>
    </div>
  );
}